type CodeSuggestion = { code: string; description?: string | null };

export interface CdtCodeEntry {
  code: string;
  count: number;
}

export interface CdtDescriptionEntry {
  description: string;
  count: number;
}

/** Build a count map for an array of strings, ignoring empty values. */
function buildCountMap(values: (string | null | undefined)[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const v of values) {
    const s = v?.trim();
    if (s) map.set(s, (map.get(s) ?? 0) + 1);
  }
  return map;
}

/**
 * Deduplicate CDT codes and return structured entries with occurrence counts.
 * Order is preserved based on first occurrence.
 */
export function deduplicateCdtCodesStructured(codeSuggestions: CodeSuggestion[]): CdtCodeEntry[] {
  const countMap = buildCountMap(codeSuggestions.map((c) => c.code));
  const seen = new Set<string>();
  const result: CdtCodeEntry[] = [];
  for (const { code } of codeSuggestions) {
    if (!code || seen.has(code)) continue;
    seen.add(code);
    result.push({ code, count: countMap.get(code)! });
  }
  return result;
}

/**
 * Deduplicate CDT code descriptions and return structured entries with occurrence counts.
 * Empty descriptions are filtered out. Order is preserved based on first occurrence.
 */
export function deduplicateCdtDescriptionsStructured(codeSuggestions: CodeSuggestion[]): CdtDescriptionEntry[] {
  const countMap = buildCountMap(codeSuggestions.map((c) => c.description));
  const seen = new Set<string>();
  const result: CdtDescriptionEntry[] = [];
  for (const { description } of codeSuggestions) {
    const d = description?.trim();
    if (!d || seen.has(d)) continue;
    seen.add(d);
    result.push({ description: d, count: countMap.get(d)! });
  }
  return result;
}

/**
 * Deduplicate CDT codes and append a plain-text multiplier label.
 * Use this for plain-string contexts (e.g. PDF, tooltip text).
 */
export function deduplicateCdtCodes(codeSuggestions: CodeSuggestion[]): string[] {
  return deduplicateCdtCodesStructured(codeSuggestions).map(({ code, count }) =>
    count > 1 ? `${code} x${count}` : code,
  );
}

/**
 * Deduplicate CDT code descriptions and append a plain-text multiplier label.
 * Use this for plain-string contexts (e.g. PDF, tooltip text).
 */
export function deduplicateCdtDescriptions(codeSuggestions: CodeSuggestion[]): string[] {
  return deduplicateCdtDescriptionsStructured(codeSuggestions).map(({ description, count }) =>
    count > 1 ? `${description} x${count}` : description,
  );
}
